<template>
  <v-card flat>
    <v-card-title>
      Driver Mileage
      <v-spacer/>
      <v-text-field
        v-model="from"
        label="From"
        type="date"
        dense
        hide-details
        style="max-width: 180px"
        class="mr-2"
      />
      <v-text-field
        v-model="to"
        label="To"
        type="date"
        dense
        hide-details
        style="max-width: 180px"
        class="mr-2"
      />
      <v-text-field
        v-model="trakToken"
        label="Onfleet trak token (optional)"
        type="text"
        dense
        hide-details
        autocomplete="off"
        style="max-width: 260px"
        class="mr-2"
        hint="enables planned-hours via app analytics endpoint"
      />
      <v-menu offset-y :close-on-content-click="false" max-width="420">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" title="How to get a trak token">
            <v-icon small>mdi-help-circle-outline</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="text-subtitle-1">Get an Onfleet trak token</v-card-title>
          <v-card-text class="text-body-2">
            <ol class="pl-4">
              <li>Click <strong>Copy script</strong> below.</li>
              <li>Open <a href="https://app.onfleet.com/dashboard#/routeAnalytics" target="_blank" rel="noopener">app.onfleet.com/dashboard#/routeAnalytics</a> and log in.</li>
              <li>Open DevTools → <strong>Console</strong> tab.</li>
              <li>Paste the script and press Enter.</li>
              <li>The token is copied to your clipboard — paste it into the field above.</li>
            </ol>
            <p class="mt-2 mb-0 text--secondary">
              Tokens expire after a session ends; re-run the script when fetches start falling back.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" small @click="copyTokenExtractorScript">
              <v-icon left small>mdi-content-copy</v-icon>
              Copy script
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
      <v-btn color="primary" :loading="loading" :disabled="!canFetch" @click="fetchReport">Fetch</v-btn>
      <v-btn class="ml-2" :disabled="!rows.length" @click="exportCsv">Export CSV</v-btn>
    </v-card-title>
    <v-card-subtitle v-if="error" class="red--text">{{ error }}</v-card-subtitle>
    <v-card-subtitle v-else-if="rows.length">
      Window: {{ dayName(from) }} {{ from }} 00:00 → {{ dayName(to) }} {{ to }} 23:59
      <span class="text--secondary">(America/Vancouver, inclusive)</span> &middot;
      Employees: {{ rows.length }} &middot;
      Matched to Onfleet: {{ matchedCount }}
      <div class="text--secondary text-caption mt-1">
        Tasks are matched by their Onfleet <strong>completion time</strong> falling inside this window.
      </div>
    </v-card-subtitle>
    <v-card-subtitle v-else class="text--secondary">
      Date range is interpreted in <strong>America/Vancouver</strong> local time, inclusive — from 00:00 on the From date through 23:59 on the To date.
      Tasks are matched by their Onfleet <strong>completion time</strong>.
    </v-card-subtitle>
    <v-alert
      v-if="rows.length && !routeSummariesUsed"
      type="warning"
      dense
      text
      class="mx-4 mb-0"
    >
      Planned hours unavailable — using fallback (actual shift span derived from task timestamps).
      Provide an Onfleet trak token above to fetch planned hours from the dashboard analytics endpoint.
      <span v-if="routeSummariesError" class="ml-2 text--secondary">({{ routeSummariesError }})</span>
    </v-alert>
    <v-card-text>
      <v-data-table
        :items="tableRows"
        :headers="headers"
        :loading="loading"
        :items-per-page="-1"
        dense
        show-expand
        single-expand
        item-key="employeeName"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template v-slot:expanded-item="{ headers: hs, item }">
          <td :colspan="hs.length" class="pa-3 grey lighten-5">
            <div v-if="!item.tasks || !item.tasks.length" class="text--secondary">
              No tasks recorded for this driver in the window.
            </div>
            <div v-else>
              <div class="mb-2 text-caption text--secondary">
                {{ item.tasks.length }} task(s) — total distance
                {{ formatNumber(taskKmSum(item.tasks), 2) }} km
              </div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">Short ID</th>
                    <th class="text-left">Recipient</th>
                    <th class="text-left">Address</th>
                    <th class="text-left">Start</th>
                    <th class="text-left">Completed</th>
                    <th class="text-end">Km</th>
                    <th class="text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in item.tasks" :key="t.id">
                    <td><code>{{ t.shortId }}</code></td>
                    <td>{{ t.recipientName }}</td>
                    <td>{{ t.address }}</td>
                    <td>{{ formatTime(t.startTime) }}</td>
                    <td>{{ formatTime(t.completionTime) }}</td>
                    <td class="text-end">{{ formatNumber((t.distanceMeters || 0) / 1000, 2) }}</td>
                    <td>
                      <span v-if="t.success" class="green--text">ok</span>
                      <span v-else class="red--text">failed</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </td>
        </template>
        <template v-slot:[`item.distanceKm`]="{ item }">
          {{ formatNumber(item.distanceKm, 2) }}
        </template>
        <template v-slot:[`item.onfleetHours`]="{ item }">
          {{ formatNumber(item.onfleetHours, 2) }}
        </template>
        <template v-slot:[`item.onfleetPlannedHours`]="{ item }">
          {{ formatNumber(item.onfleetPlannedHours, 2) }}
        </template>
        <template v-slot:[`item.onfleetDurationDeltaHours`]="{ item }">
          {{ formatSignedNumber(item.onfleetDurationDeltaHours, 2) }}
        </template>
        <template v-slot:[`item.onfleetEndTimeDeltaHours`]="{ item }">
          {{ formatSignedNumber(item.onfleetEndTimeDeltaHours, 2) }}
        </template>
        <template v-slot:[`item.hoursWorked`]="{ item }">
          {{ formatNumber(item.hoursWorked, 2) }}
        </template>
        <template v-for="role in roleColumns" v-slot:[`item.${roleKey(role)}`]="{ item }">
          {{ formatNumber(item[roleKey(role)], 2) }}
        </template>
      </v-data-table>
    </v-card-text>
    <v-snackbar v-model="snackbar" timeout="3500" color="success">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import api from "@/api";
import moment from "moment";
import urlState from "@/router/urlState";

export default {
  name: "DriverMileage",
  mixins: [urlState],
  data() {
    return {
      from: '',
      to: '',
      trakToken: '',
      loading: false,
      rows: [],
      windowFrom: null,
      windowTo: null,
      error: null,
      routeSummariesUsed: false,
      routeSummariesError: null,
      sortBy: 'distanceKm',
      sortDesc: true,
      snackbar: false,
      snackbarText: '',
    };
  },
  computed: {
    canFetch() {
      return this.from && this.to && this.from <= this.to && !this.loading;
    },
    matchedCount() {
      return this.rows.filter(r => r.driver).length;
    },
    roleColumns() {
      const seen = new Set();
      for (const r of this.rows) {
        for (const role of Object.keys(r.hoursByRole || {})) seen.add(role);
      }
      return [...seen].sort();
    },
    headers() {
      const base = [
        {text: 'Employee', value: 'employeeName'},
        {text: 'Phone', value: 'employeePhone'},
        {text: 'Match', value: 'matchedBy'},
        {text: 'Tasks', value: 'taskCount', align: 'end'},
        {text: 'Failed', value: 'failedTaskCount', align: 'end'},
        {text: 'Km', value: 'distanceKm', align: 'end'},
        {text: 'Onfleet hrs', value: 'onfleetHours', align: 'end'},
        {text: 'Planned hrs', value: 'onfleetPlannedHours', align: 'end'},
        {text: 'Duration Δ', value: 'onfleetDurationDeltaHours', align: 'end'},
        {text: 'End time Δ', value: 'onfleetEndTimeDeltaHours', align: 'end'},
      ];
      const roleHeaders = this.roleColumns.map(role => ({
        text: role,
        value: this.roleKey(role),
        align: 'end',
      }));
      const tail = [
        {text: 'Total hrs', value: 'hoursWorked', align: 'end'},
      ];
      return [...base, ...roleHeaders, ...tail];
    },
    tableRows() {
      return this.rows.map(r => {
        const row = {
          employeeName: r.employee && r.employee.name,
          employeePhone: r.employee && r.employee.phone,
          matchedBy: r.matchedBy || '',
          taskCount: r.taskCount || 0,
          failedTaskCount: r.failedTaskCount || 0,
          distanceKm: r.distanceKm || 0,
          onfleetHours: r.onfleetHours || 0,
          onfleetPlannedHours: r.onfleetPlannedHours || 0,
          onfleetDurationDeltaHours: r.onfleetDurationDeltaHours || 0,
          onfleetEndTimeDeltaHours: r.onfleetEndTimeDeltaHours || 0,
          hoursWorked: r.hoursWorked || 0,
          tasks: r.tasks || [],
        };
        for (const role of this.roleColumns) {
          row[this.roleKey(role)] = (r.hoursByRole && r.hoursByRole[role]) || 0;
        }
        return row;
      });
    },
  },
  mounted() {
    this.syncToUrl({param: 'from', urlParam: 'from', initFromRoute: true});
    this.syncToUrl({param: 'to', urlParam: 'to', initFromRoute: true});
    if (this.canFetch) this.fetchReport();
  },
  methods: {
    roleKey(role) {
      return 'role_' + role.replace(/[^a-zA-Z0-9]+/g, '_');
    },
    formatNumber(n, digits = 1) {
      if (n == null || n === 0) return '';
      return Number(n).toFixed(digits);
    },
    formatSignedNumber(n, digits = 1) {
      if (n == null || n === 0) return '';
      const v = Number(n).toFixed(digits);
      return n > 0 ? `+${v}` : v;
    },
    dayName(yyyymmdd) {
      const m = moment(yyyymmdd, 'YYYY-MM-DD', true);
      return m.isValid() ? m.format('dddd') : '';
    },
    formatTime(ms) {
      if (!ms) return '';
      const d = new Date(ms);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    taskKmSum(tasks) {
      return (tasks || []).reduce((s, t) => s + (t.distanceMeters || 0), 0) / 1000;
    },
    copyTokenExtractorScript() {
      const script = `(() => {
  const TOKEN_RE = /\\b[a-f0-9]{64}\\b/;
  const hits = [];
  const scanStorage = (store, label) => {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      const v = store.getItem(k) ?? '';
      const m = v.match(TOKEN_RE);
      if (m) hits.push({source: \`\${label}["\${k}"]\`, token: m[0]});
    }
  };
  scanStorage(localStorage, 'localStorage');
  scanStorage(sessionStorage, 'sessionStorage');
  const cookieMatch = document.cookie.match(TOKEN_RE);
  if (cookieMatch) hits.push({source: 'cookie', token: cookieMatch[0]});
  if (!hits.length) {
    console.warn('No 64-char hex token found. Try DevTools → Network → any analytics request → Request Headers → trak-access-token.');
    return;
  }
  console.table(hits);
  const token = hits[0].token;
  navigator.clipboard.writeText(token)
    .then(() => console.log(\`Copied to clipboard: \${token}\`))
    .catch(() => console.log(\`Token: \${token} (clipboard write failed — copy manually)\`));
})();`;
      const done = () => {
        this.snackbarText = 'Script copied. Paste it into the Onfleet app console.';
        this.snackbar = true;
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(script).then(done).catch(() => {
          this.fallbackCopy(script);
          done();
        });
      } else {
        this.fallbackCopy(script);
        done();
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    },
    fetchReport() {
      if (!this.canFetch) return;
      this.loading = true;
      this.error = null;
      const params = {from: this.from, to: this.to};
      if (this.trakToken) params.trakToken = this.trakToken;
      api.get('/v2/onfleet/driver-mileage', {params})
        .then(({data}) => {
          this.rows = data.rows || [];
          this.windowFrom = data.from;
          this.windowTo = data.to;
          this.routeSummariesUsed = !!data.routeSummariesUsed;
          this.routeSummariesError = data.routeSummariesError || null;
        })
        .catch(e => {
          this.error = (e.response && e.response.data && e.response.data.error) || e.message;
          this.rows = [];
          this.routeSummariesUsed = false;
          this.routeSummariesError = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    csvEscape(value) {
      if (value == null) return '';
      const s = String(value);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    },
    exportCsv() {
      const columns = this.headers.map(h => ({text: h.text, value: h.value}));
      const headerLine = columns.map(c => this.csvEscape(c.text)).join(',');
      const lines = this.tableRows.map(row =>
        columns.map(c => {
          const v = row[c.value];
          if (typeof v === 'number') return this.csvEscape(v.toFixed(2));
          return this.csvEscape(v);
        }).join(',')
      );
      const csv = [headerLine, ...lines].join('\n');
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `driver-mileage_${this.from}_to_${this.to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>
